import { ActiveBotSession, fetchActiveBotSessions } from './botApi';
import { listActiveStandaloneSessions } from './standaloneSessionApi';
import { describeBotApiError } from '../utils/botApiErrors';
import { parseServerTimestampMs } from '../utils/serverTimestamp';
import { BOT_API_COPY } from '../constants/botApi';
import { WATCH_SESSION_COPY } from '../constants/watchSession';
import type { WatchableSession } from '../types/liveSession';

export interface WatchableSessionsResult {
  /** Newest first. */
  sessions: WatchableSession[];
  /** One user-facing message per list that failed to load; the other list still shows. */
  errors: string[];
}

const toWatchableBot = (bot: ActiveBotSession): WatchableSession => ({
  sessionId: bot.botId as string,
  mode: 'bot',
  sessionName: bot.sessionName || '',
  ownerName: bot.ownerName || bot.ownerEmail || null,
  startedAt: bot.startedAt ?? null,
  isOwn: !!bot.isOwn,
  websocketUrl: bot.websocketUrl ?? null,
  abandoned: !!bot.abandoned,
});

const byNewestStart = (first: WatchableSession, second: WatchableSession): number =>
  (parseServerTimestampMs(second.startedAt) ?? 0) - (parseServerTimestampMs(first.startedAt) ?? 0);

/**
 * Every live call in the tenant: bot calls from the backend (GET
 * /api/bots/active, the user's own included and flagged `isOwn`) and desktop
 * calls from the services (GET /api/standalone/sessions/active, which leaves
 * out the user's own). Both lists load in parallel; one failing does not hide
 * the other.
 */
export const fetchWatchableSessions = async (): Promise<WatchableSessionsResult> => {
  const [botResult, standaloneResult] = await Promise.allSettled([
    fetchActiveBotSessions<ActiveBotSession>(),
    listActiveStandaloneSessions(),
  ]);

  const sessions: WatchableSession[] = [];
  const errors: string[] = [];

  if (botResult.status === 'fulfilled') {
    sessions.push(...botResult.value.filter((bot) => bot?.botId).map(toWatchableBot));
  } else {
    console.warn('[watchableSessions] Bot calls failed to load:', botResult.reason);
    errors.push(describeBotApiError(botResult.reason, BOT_API_COPY.sessionsForbidden, WATCH_SESSION_COPY.botSessionsFailed));
  }

  if (standaloneResult.status === 'fulfilled') {
    sessions.push(...standaloneResult.value);
  } else {
    console.warn('[watchableSessions] Desktop calls failed to load:', standaloneResult.reason);
    errors.push(
      describeBotApiError(
        standaloneResult.reason,
        WATCH_SESSION_COPY.standaloneSessionsForbidden,
        WATCH_SESSION_COPY.standaloneSessionsFailed
      )
    );
  }

  return { sessions: sessions.sort(byNewestStart), errors: Array.from(new Set(errors)) };
};
